<script
    setup
    lang="ts"
    generic="R extends boolean = false, V extends SID = SID"
>
import { faSearch, faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { MrInput, MrSelectInput, MrSelectItem } from '#components';
import type {
    InputSizeType,
    SelectItemId as SID,
    SelectItemType,
} from '#imports';

const {
    returnObject: _returnObject,
    hideOpenIndicator,
    label = '',
    placeholder = '',
    options = [],
    required,
    description = '',
    size = InputSize.dense,
    invalid,
    mobileModal,
    disableLocalSearch,
    loading,
    emptyListMessage = 'نتیجه‌ای پیدا نشد.',
    showGroupsInSearch,
    clearable,
    disabled,
    multiSelected,
    disableSearch,
    minHeight,
    minWidth,
    iconSize = 'x-large',
    readonly,
} = defineProps<IProps>();
const emit = defineEmits<{
    close: [];
    'close-button': [];
    'select-option': [];
    'back-clicked': [];
    clear: [];
    open: [];
    'open:after': [];
    search: [value: string];
    selected: [value: SelectItemType<V>[]];
    filled: [];
    emptied: [value: string];
}>();
interface IProps {
    returnObject?: R;
    hideOpenIndicator?: boolean;
    label?: string;
    placeholder?: string;
    options?: SelectItemType<V>[];
    required?: boolean;
    description?: string;
    size?: InputSizeType;
    icon?: IconDefinition;
    invalid?: boolean;
    hideDescription?: boolean;
    mobileModal?: boolean;
    hideLabel?: boolean;
    hideBack?: boolean;
    pagingModalAnimation?: boolean;
    disableLocalSearch?: boolean;
    loading?: boolean;
    emptyListMessage?: string;
    minWidth?: number;
    minHeight?: number;
    showGroupsInSearch?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    multiSelected?: boolean;
    disableSearch?: boolean;
    fullWidth?: boolean;
    scrollToContainer?: boolean;
    iconSize?: SizeConditional<IconSize>;
    readonly?: boolean;
}

const { isMobile } = useSize();

const modelValue = defineModel<
    (R extends true ? SelectItemType<V> : V) | null
>();

const scrollContainer = ref<HTMLDivElement>();
const select = ref<InstanceType<typeof MrSelectInput>>();
const modalSearchBox = ref<InstanceType<typeof MrInput>>();
const selected = ref<SelectItemType[]>([]);
const selectedView = ref<HTMLDivElement[]>();
const selectItem = ref<InstanceType<any>[]>();

const returnObject = computed(
    () => _returnObject === true || (_returnObject as any) === ''
);

const onToggle = (checked: boolean | undefined, option: SelectItemType<V>) => {
    if (option.isCheckbox) {
        onSelect({ ...option, checked: !!checked });
    }
};
const labelModelValue = (optionId: SID) =>
    !!selected.value.find(({ id }) => id === optionId);

const local = ref<SelectItemType<V>>();
const searchKeyword = ref('');
const opened = ref(false);
const selectedItem = ref(false);
const currentFocusedIndex = ref(0);
const groupOptions = (
    options: SelectItemType<V>[],
    parentId?: string
): SelectItemType<V>[] =>
    options.reduce((group, option) => {
        const key = parentId ? `${parentId}-${option.id}` : `${option.id}`;
        if (option.subItems && option.subItems.length) {
            return [
                ...group,
                {
                    ...option,
                    key,
                } as SelectItemType<V>,
                ...groupOptions(option.subItems, key),
            ];
        } else {
            return [
                ...group,
                {
                    ...option,
                    key,
                } as SelectItemType<V>,
            ];
        }
    }, [] as SelectItemType<V>[]);
const groupedOptions = computed<SelectItemType<V>[]>(() =>
    groupOptions(options)
);
const localOptions = computed<SelectItemType<V>[]>(() => {
    if (searchKeyword.value && !disableLocalSearch && opened.value) {
        return groupOptions(options.filter(filter)).filter(
            d => filter(d) && (showGroupsInSearch || !d.isGroup)
        );
    } else if (searchKeyword.value) {
        return groupedOptions.value.filter(
            o => showGroupsInSearch || !o.isGroup
        );
    } else {
        return groupedOptions.value;
    }
});

// options and selected will be in sync now
watch(
    () => options,
    () => {
        selected.value = localOptions.value.filter(({ checked }) => checked);
    },
    { immediate: true }
);
const filled = computed(() => localOptions.value.some(item => !item.fixedItem));
const searchPlaceholder = computed(() => `جستجوی ${placeholder}`);

const localPlaceholder = computed(() => {
    if (selected.value.length === 0 && multiSelected && local.value) {
        // only happen on multi select
        return local.value?.label;
    }
    return placeholder;
});

const localInvalid = computed(() => !opened.value && invalid);
const localDesc = computed(() => {
    if (localInvalid.value && required) {
        return 'این فیلد نمی‌تواند خالی باشد.';
    } else {
        return description;
    }
});
const filter = (option: SelectItemType<V>) => {
    if (option.ignoreSearch) {
        return false;
    }
    if (option.fixedItem) {
        return true;
    }
    return (
        searchPersianInStrings(searchKeyword.value, [
            option.label,
            option.subTitle ?? '',
        ]).matched ||
        option.subItems?.some(filter) ||
        false
    );
};
const onSearchKeyDown = (e: KeyboardEvent) => {
    switch (e.code) {
        case 'ArrowDown':
            focusNext();
            break;
        case 'ArrowUp':
            focusPrev();
            break;
        case 'Escape':
            close();
            break;
        default:
            break;
    }
};
const setFirstFocus = () => {
    if (isMobile.value) {
        return;
    }
    currentFocusedIndex.value = 0;
    scrollContainer.value?.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
};

const focusNext = () => {
    if (isMobile.value) {
        return;
    }
    let haveNext = false;
    let current = currentFocusedIndex.value + 1;
    while (localOptions.value[current]) {
        if (!localOptions.value[current]?.isGroup) {
            haveNext = true;
            break;
        }
        current++;
    }
    if (haveNext) {
        currentFocusedIndex.value = current;
        scrollContainer.value?.scrollBy({
            top: selectItem.value?.[current]?.$el.clientHeight,
            behavior: 'smooth',
        });
    }
};
const focusPrev = () => {
    if (isMobile.value) {
        return;
    }
    let havePrev = false;
    let current = currentFocusedIndex.value - 1;
    while (localOptions.value[current]) {
        if (!localOptions.value[current]?.isGroup) {
            havePrev = true;
            break;
        }
        current--;
    }
    if (havePrev) {
        currentFocusedIndex.value = current;
        scrollContainer.value?.scrollBy({
            top: -selectItem.value?.[current]?.$el.clientHeight,
            behavior: 'smooth',
        });
    }
};
const closeButtonClicked = () => {
    emit('close-button');
    close();
};
const onSubmit = () => {
    const firstOption = localOptions.value[currentFocusedIndex.value];
    if (firstOption) {
        onSelect(firstOption);
    }
};

const containerWidth = ref<number>();

onMounted(() => {
    if (multiSelected) {
        useResizeObserver(select.value?.$el, () => {
            containerWidth.value = select.value?.$el.offsetWidth;
            displayOverflow.value = 0;
        });
    }
});
// calculate width of items in display
const displayWidth = computed(() =>
    selectedView.value?.reduce((sum, { offsetWidth }) => sum + offsetWidth, 0)
);

const displayOverflow = ref<number>();
// call on displayWidth resize
// start from showing one item and then go up
watch([displayWidth, selected, containerWidth], () => {
    if (
        containerWidth.value &&
        displayWidth.value &&
        displayWidth.value > containerWidth.value - 50
    ) {
        displayOverflow.value = 1;
    } else if (!displayWidth.value) {
        displayOverflow.value = undefined;
    }
});

const overflowCount = computed(() => {
    const leftOver = selected.value.length - (displayOverflow.value || 0);
    return displayOverflow.value && leftOver > 0 && leftOver;
});

const onSelect = (option: SelectItemType<V>) => {
    selectedItem.value = true;
    emit('select-option');
    local.value = option;
    // checkbox select
    if (option?.isCheckbox) {
        if (option?.checked) {
            selected.value = [option, ...selected.value];
        } else if (!option?.checked) {
            selected.value = selected.value.filter(
                ({ id }) => id !== option.id
            );
        }
        multiSelected && emit('selected', selected.value as any);
        // remove search value on select so user can see all the options again
        searchKeyword.value = '';
        // reset overflow for new selects
        displayOverflow.value = undefined;
        return;
    }
    searchKeyword.value = option.label;
    close();
    select.value?.blur();
};
const close = () => select.value?.close();

const open = () => select.value?.open();
const focus = () => {
    select.value?.focus();
    searchKeyword.value = '';
};
const clear = () => {
    local.value = undefined;
    emit('clear');
};
const onOpen = () => {
    if (opened.value) {
        return;
    }
    selectedItem.value = false;
    searchKeyword.value = '';
    emit('open');
    opened.value = true;
    setTimeout(() => {
        if (isMobile.value && mobileModal) {
            modalSearchBox.value?.focus();
        }
        emit('open:after');
    });
};
const onClose = () => {
    if (!opened.value) {
        return;
    }
    if (!selectedItem.value) {
        emit('search', '');
        nextTick(() => {
            searchKeyword.value = local.value?.label || '';
        });
    }
    emit('close');
    opened.value = false;
};
watch(
    [modelValue, returnObject],
    ([value, returnObject]) => {
        const vid = returnObject ? (value as SelectItemType<V>)?.id : value;
        local.value = groupedOptions.value.find(({ id }) => id === vid);
        if (!opened.value) {
            searchKeyword.value = local.value?.label || '';
        }
        setFirstFocus();
    },
    { immediate: true }
);
watch(local, value => {
    if (!value && !clearable) {
        return;
    }
    modelValue.value = (returnObject.value ? value : value?.id) as any; // not important inside,
    // interfacing is more important
    setFirstFocus();
});

watch(searchKeyword, val => {
    if (opened.value) {
        emit('search', val);
        setFirstFocus();
    }
});
watch(filled, val => {
    if (val) {
        emit('filled');
    } else {
        emit('emptied', searchKeyword.value);
    }
});

defineExpose({
    focus,
    open,
    close,
    opened,
    scrollContainer,
});
</script>

<template>
    <mr-select-input
        ref="select"
        v-model="searchKeyword"
        :placeholder="localPlaceholder"
        :icon
        :size
        :invalid="localInvalid"
        :hide-indicator="hideOpenIndicator"
        :hide-description
        :searchable="!disableSearch && !readonly"
        :description="localDesc"
        :mobile-modal
        :label
        :hide-label
        :paging-modal-animation
        :required
        :clearable
        :disabled
        :readonly
        :full-width
        :scroll-to-container
        @clear="clear"
        @open="onOpen"
        @close="onClose"
        @submit="onSubmit"
        @keydown="onSearchKeyDown"
    >
        <template #header>
            <DelayHydration>
                <mr-modal-header
                    v-if="mobileModal"
                    class="modal-header"
                    :title="label"
                    :hide-back
                    hide-padding
                    @close="closeButtonClicked"
                    @back="emit('back-clicked')"
                >
                    <div class="input-header-container">
                        <mr-input
                            ref="modalSearchBox"
                            v-model="searchKeyword"
                            class="modal-search-box"
                            :hide-description
                            bypass-i-m-e-composition
                            :required
                            :icon="faSearch"
                            :clearable
                            :readonly
                            :placeholder="searchPlaceholder"
                            autocomplete="off"
                            type="search"
                            @submit="onSubmit"
                            @search="searchKeyword = $event"
                            @keydown="onSearchKeyDown"
                            @clear="clear"
                        />
                    </div>
                </mr-modal-header>
            </DelayHydration>
        </template>
        <template v-if="multiSelected" #selected>
            <ClientOnly>
                <div class="selected-view-container">
                    <div
                        v-for="item in selected.slice(0, displayOverflow)"
                        :key="item.id"
                        ref="selectedView"
                        :class="{
                            [item.color as string]: item?.color,
                            default: !item?.color,
                        }"
                    >
                        {{ item.label }}
                    </div>
                    <div v-if="overflowCount" class="default">
                        {{ overflowCount }}
                    </div>
                </div>
            </ClientOnly>
        </template>
        <ClientOnly>
            <div class="mr-select-scroll-container">
                <div
                    ref="scrollContainer"
                    class="mr-select-items-container"
                    :style="{
                        '--min-width': `${minWidth}px`,
                        '--min-height': `${minHeight}px`,
                        '--m-max-height': mobileModal ? '100%' : '320px',
                    }"
                >
                    <mr-bullet-list-loader
                        v-show="loading"
                        class="list-loader"
                    />
                    <template v-if="!loading">
                        <mr-select-item
                            v-for="(item, index) in localOptions"
                            ref="selectItem"
                            :key="item.key"
                            :item
                            :focused="index === currentFocusedIndex"
                            :model-value="labelModelValue(item.id)"
                            @update:model-value="onToggle($event, item)"
                            @click="onSelect(item)"
                            @mouseover="currentFocusedIndex = index"
                        />
                    </template>
                    <mr-typography
                        v-if="!localOptions.length && !loading"
                        size="large"
                        weight="700"
                        color="var(--cool-gray-400)"
                        class="no-option"
                    >
                        <mr-icon
                            :icon="faXmark"
                            color="var(--cool-gray-400)"
                            :size="iconSize"
                        />
                        {{ emptyListMessage }}
                    </mr-typography>
                </div>
            </div>
        </ClientOnly>
    </mr-select-input>
</template>

<style lang="scss" scoped>
.no-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100px;
}
.mr-select-scroll-container {
    padding: 4px 0 4px 8px;
}
.mr-select-items-container {
    min-width: var(--min-width);
    min-height: var(--min-height);
    max-height: 320px;
    overflow-y: scroll;
    .list-loader {
        margin-right: 12px;
    }
}

@media (min-width: $app-desktop-min-width) {
    .mr-select-items-container {
        &::-webkit-scrollbar {
            width: 5px;
        }
        &::-webkit-scrollbar-track {
            background: usePalette($p-gray, $p-200);
            border-radius: 10%;
        }
        &::-webkit-scrollbar-thumb {
            background: usePalette();
            border-radius: 10%;
        }
    }
}

@media (max-width: $app-mobile-max-width) {
    .mr-select-scroll-container {
        flex: 1;
        max-height: calc(100% - 132px);
        padding: 0;
    }
    .mr-select-items-container {
        max-height: var(--m-max-height);
        height: 100%;
        padding: 0 8px;
    }

    .input-header-container {
        padding: 0 8px 8px 8px;
        .modal-search-box {
            border-radius: 8px !important;
            overflow: hidden;
        }
    }
}

.selected-view-container {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 0 10px;
    > div {
        padding: 0 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 10000px;
        height: 30px;
        min-width: 30px;
    }
    .default {
        @include tg-m($w4, usePalette($p-blue, $p-700));
        background: usePalette($p-blue, $p-200);
    }
    .green {
        @include tg-m($weight: $w4, $color: usePalette($p-green, $p-700));
        background: usePalette($p-green, $p-200);
    }

    .red {
        @include tg-m($weight: $w4, $color: usePalette($p-red, $p-700));
        background: usePalette($p-red, $p-200);
    }

    .blue {
        @include tg-m($weight: $w4, $color: usePalette($p-blue, $p-700));
        background: usePalette($p-blue, $p-200);
    }

    .yellow {
        @include tg-m($weight: $w4, $color: usePalette($p-yellow, $p-700));
        background: usePalette($p-yellow, $p-200);
    }

    .gray {
        @include tg-m($weight: $w4, $color: usePalette($p-cool-gray, $p-700));
        background: usePalette($p-cool-gray, $p-200);
    }
}
</style>
